import React, { useMemo, useState } from 'react';
import {
  convertCentsToMoney,
  displaySuccess,
  formatPhoneNumber,
  getLocaleDateTimeString,
  toTitleCase,
} from '../../../../utils';
import {
  IUserDetail,
  IUserOrganizationDetails,
  UserDetailStatus,
} from '../../../../ts/interface/userDetail.interface';
import { PropertyList } from '../../../../components/Redesing/property-list';
import { PropertyListItem } from '../../../../components/Redesing/property-list-item';
import { ISystem } from '../../../../ts/interface/system.interface';
import isEmpty from 'lodash/isEmpty';
import MovaticCustomModal from '../../../../components/Modal/MovaticCustomModal';
import UserCreditWaller from '../../userCreditWaller';
import AddStripeCard from '../../../../components/AddStripeCard';
import { IUserCard } from '../../../../ts/interface/userCard.interface';
import {
  clearUserBalance,
  getUserStripeSetupIntent,
  sendTosEmail,
  updateUserDelinquency,
} from '../../../../api/users';
import { useParams } from 'react-router-dom';
import Divider from '@mui/material/Divider';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import CardView from '../../../../components/Redesing/card-view';
import IconButtonMenu from '../../../../components/Redesing/icon-button-menu';
import {
  MovaticFieldGroupOutlinedInput,
  MovaticFieldGroupRadio,
  MovaticFieldGroupText,
} from '../../../../components/Redesing/movatic-field-group';
import CustomAsyncToast from '../../../../components/Redesing/custom-async-toast';
import { createCharge } from '../../../../api/manualCharge';
import SaveIcon from '@mui/icons-material/Save';
import useIsMobileDevice from '../../../../hooks/mobileDevices/useIsMobileDevice';
import { useMutation } from '@tanstack/react-query';

import { SeverityPill } from '../../../../components/Redesing/severity-pill';
import { PaginationTable } from '../../../../components/Redesing/table/pagination-table';
import useOrganization from '../../../../hooks/use-organization';

interface GeneralUserInfoBannerProps {
  userDetails: IUserDetail | IUserOrganizationDetails;
  privateSystem: boolean;
  system: ISystem | ISystem[];
  onSuccess: Function;
  paymentInfoDocumentationLink?: string;
  creditCards?: any[];
  userDelinquent?: boolean;
}

export enum IChargeRecipient {
  SYSTEM = 'system',
  PARTNER = 'partner',
}

const GeneralUserInfoBanner = ({
  userDetails,
  privateSystem,
  system,
  onSuccess,
  paymentInfoDocumentationLink,
  creditCards,
  userDelinquent,
}: GeneralUserInfoBannerProps) => {
  const {
    appVersion,
    autoGenNumber,
    deviceModel,
    deviceOS,
    deviceOSVersion,
    phone,
    // amount_due,
    joined,
    emailAddress,
    acceptedTOSOn,
    walletBalance,
    hasWallet,
  } = userDetails;

  const organizationInfo = useOrganization();

  const deviceOSInfo = deviceOS ? `- ${deviceOS} (${deviceOSVersion})` : '';
  const deviceInfoString = deviceModel ? `${deviceModel} ${deviceOSInfo}` : '';

  const { userPhone = '' } = useParams<{ userPhone: string }>();
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showCreditUserModal, setShowCreditUserModal] = useState(false);
  const [showAddCreditCardModal, setShowAddCreditCardModal] = useState(false);
  const [showClearBalanceModal, setShowClearBalanceModal] = useState(false);
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeAmountDescription, setChargeAmountDescription] = useState('');
  const isMobile = useIsMobileDevice();
  const isMedium = useIsMobileDevice(1300);

  const userCard = useMemo<IUserCard>(() => {
    if (!creditCards || creditCards.length === 0) return {};
    const card: any = creditCards[0];
    return {
      brand: card.brand,
      exp_month: card.exp_month?.toString(),
      exp_year: card.exp_year?.toString(),
      last4: card.last4,
      country: card.country,
      delinquent: userDelinquent ?? false,
    };
  }, [creditCards, userDelinquent]);
  const [chargeRecipient, setChargeRecipient] = useState<IChargeRecipient>(
    IChargeRecipient.SYSTEM
  );

  const isSystemArray = Array.isArray(system);

  const usesWallet = useMemo(() => {
    // check if system is an array
    if (isSystemArray) {
      // check if aleast on system has wallet_required
      const hasWallet = system.some((sys) => sys.wallet_required);
      return Boolean(hasWallet);
    }
    return Boolean(system.wallet_required);
  }, [system, isSystemArray]);

  const useBillingLive = useMemo(() => {
    // check if system is an array
    if (isSystemArray) {
      // check if aleast on system has billingLive
      const hasBillingLive = system.some((sys) => sys.billingLive);
      return Boolean(hasBillingLive);
    }
    return Boolean(system.billingLive);
  }, [system, isSystemArray]);

  const usePartnerName = useMemo(() => {
    if (isSystemArray) {
      return system.map((sys) => sys.partner_name).join(', ');
    }
    return system.partner_name;
  }, [system, isSystemArray]);

  const useSystemNames = useMemo(() => {
    if (isSystemArray) {
      return system.map((sys) => sys.name).join(', ');
    }
    return system.name;
  }, [system, isSystemArray]);

  const calculateArrayBtns = useMemo(() => {
    return [
      {
        label: 'Charge Card',
        dataId: 'charge-card-button',
        onClick: () => setShowChargeModal(true),
        startIcon: <PaymentIcon />,
        shouldRender: Boolean(userCard && !userCard.delinquent),
      },
      {
        label: 'Credit',
        onClick: () => setShowCreditUserModal(true),
        dataId: 'credit-user-button',
        startIcon: <PaymentIcon />,
        shouldRender: Boolean(usesWallet) && hasWallet,
      },
      {
        label: 'Set Non-Delinquent',
        dataId: 'set-non-delinquent-button',
        onClick: () => setShowConfirmationModal(true),
        startIcon: <CheckCircleIcon />,
        shouldRender: Boolean(userCard && userCard.delinquent),
      },
      {
        label: 'Add Card',
        dataId: 'add-card-button',
        onClick: () => setShowAddCreditCardModal(true),
        startIcon: <AddIcon />,
        shouldRender: Boolean(isEmpty(userCard)),
      },
      // {
      //   label: 'Clear Balance',
      //   dataId: 'clear-balance-button',
      //   onClick: () => setShowClearBalanceModal(true),
      //   startIcon: <CloseIcon />,
      //   shouldRender: Boolean(
      //     userDetails.amount_due != null && userDetails.amount_due > 0
      //   ),
      // },
    ];
  }, [usesWallet, hasWallet, userCard]);

  const sendTosEmailMutation = useMutation({
    mutationFn: sendTosEmail,
  });

  const resetChargeModalFields = () => {
    setChargeAmount('');
    setChargeAmountDescription('');
    setChargeRecipient(IChargeRecipient.SYSTEM);
    setShowChargeModal(false);
  };

  const handleRadioConfigurationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    setChargeRecipient(value as IChargeRecipient);
  };

  const getStripeClientSecret = async () => {
    const response = await getUserStripeSetupIntent(userPhone);
    return response?.client_secret;
  };

  const systemOptions = useMemo(() => {
    if (isSystemArray) {
      return system.flatMap((sys) => [
        {
          id: IChargeRecipient.SYSTEM,
          label: `System ("${sys.name}")`,
        },
        {
          id: IChargeRecipient.PARTNER,
          label: `Partner ("${sys.partner_name}")`,
        },
      ]);
    }
    return [
      {
        id: IChargeRecipient.SYSTEM,
        label: `System ("${system.name}")`,
      },
      {
        id: IChargeRecipient.PARTNER,
        label: `Partner ("${system.partner_name}")`,
      },
    ];
  }, [system, isSystemArray]);

  return (
    <>
      <CardView
        headerActions={
          !acceptedTOSOn && (
            <IconButtonMenu
              buttonLimit={isMobile ? 0 : 1}
              slice={isMobile ? 0 : 1}
              buttons={[
                {
                  label: 'Send ToS Email',
                  onClick: () => sendTosEmailMutation.mutate(userDetails.id),
                  startIcon: <i className={'fa fa-envelope'} />,
                  disabled: sendTosEmailMutation.isLoading,
                },
              ]}
            />
          )
        }
        content={
          <PropertyList>
            <PropertyListItem divider label={'Status'}>
              {userDetails.banned ||
              userDetails?.status === UserDetailStatus.BLACKLISTED ? (
                <SeverityPill color={'error'}>Banned</SeverityPill>
              ) : (
                <SeverityPill color={'success'}>Authorized</SeverityPill>
              )}
            </PropertyListItem>
            <PropertyListItem divider label={'Email'} value={emailAddress} />
            <PropertyListItem
              divider
              label={autoGenNumber ? 'Temporary ID' : 'Phone'}
              value={autoGenNumber ? phone : formatPhoneNumber(phone)}
            />
            <PropertyListItem divider label="App Version" value={appVersion} />
            <PropertyListItem divider label="Device" value={deviceInfoString} />
            {/*<PropertyListItem label="Accepted ToS">*/}
            {/*  {acceptedTOSOn ? (*/}
            {/*    getLocaleDateTimeString(acceptedTOSOn)*/}
            {/*  ) : (*/}
            {/*    <SeverityPill color={'error'}>N/A</SeverityPill>*/}
            {/*  )}*/}
            {/*</PropertyListItem>*/}

            {/* {amount_due > 0 && ( */}
            {/*   <> */}
            {/*     <Divider /> */}
            {/*     <PropertyListItem */}
            {/*       divider */}
            {/*       label="Account Balance" */}
            {/*       value={convertCentsToMoney(amount_due)} */}
            {/*     /> */}
            {/*   </> */}
            {/* )} */}
            {privateSystem && joined && (
              <>
                <Divider />
                <PropertyListItem
                  label="Joined"
                  value={getLocaleDateTimeString(joined)}
                />
              </>
            )}
          </PropertyList>
        }
        title={'Basic Details'}
      />

      <CardView
        documentationLink={paymentInfoDocumentationLink}
        content={
          <PropertyList>
            {(useBillingLive || Boolean(organizationInfo.id)) && (
              <>
                {!isEmpty(userCard) ? (
                  <PropertyList>
                    <PropertyListItem
                      divider
                      label={'Type'}
                      value={toTitleCase(userCard.brand)}
                    />
                    <PropertyListItem
                      divider
                      label="Expiration Date"
                      value={`${userCard.exp_month}/${userCard.exp_year}`}
                    />
                    <PropertyListItem divider label="Last 4" value={userCard.last4} />
                    <PropertyListItem divider label="Origin" value={userCard.country} />
                    <PropertyListItem label="Delinquent">
                      {userCard.delinquent ? (
                        <SeverityPill color={'error'}>Yes</SeverityPill>
                      ) : (
                        <SeverityPill color={'success'}>No</SeverityPill>
                      )}
                    </PropertyListItem>
                  </PropertyList>
                ) : isEmpty(userCard) ? (
                  <PropertyList>
                    <Divider />
                    <PropertyListItem label={'No credit card on file'} value={''} />
                  </PropertyList>
                ) : null}

                {usesWallet && hasWallet ? (
                  <PropertyList>
                    <Divider />
                    <PropertyListItem
                      label={'Wallet Balance'}
                      value={convertCentsToMoney(walletBalance)}
                    />
                  </PropertyList>
                ) : null}
                {showConfirmationModal && (
                  <MovaticCustomModal
                    useContentDividers
                    open={showConfirmationModal}
                    onClose={() => setShowConfirmationModal(false)}
                    title="Set Non-Delinquent"
                    content={
                      <div>
                        {`Are you sure that would like to Set Non-Delinquent ${userDetails.fullName}?`}
                      </div>
                    }
                    action={
                      <>
                        <IconButtonMenu
                          buttons={[
                            {
                              label: 'Cancel',
                              onClick: () => setShowConfirmationModal(false),
                            },
                            {
                              label: 'Submit',
                              onClick: () => {
                                CustomAsyncToast({
                                  promise: () => updateUserDelinquency(userPhone),
                                  successMessage: () => 'User Delinquency Status Updated',
                                  errorMessage: 'User Delinquency Status Update Failed',
                                  loadingMessage: 'Updating User Delinquency Status...',
                                }).then((r) => {
                                  if (r) {
                                    onSuccess();
                                    setShowConfirmationModal(false);
                                  }
                                });
                              },
                              startIcon: <SaveIcon />,
                            },
                          ]}
                        />
                      </>
                    }
                  />
                )}
                {showCreditUserModal && (
                  <UserCreditWaller
                    onSuccess={() => onSuccess()}
                    showModal={showCreditUserModal}
                    close={() => setShowCreditUserModal(false)}
                  />
                )}
                {showAddCreditCardModal && (
                  <AddStripeCard
                    showModal={showAddCreditCardModal}
                    close={() => setShowAddCreditCardModal(false)}
                    success={displaySuccess}
                    getClientSecret={getStripeClientSecret}
                    getUserCard={() => onSuccess()}
                  />
                )}
                {showClearBalanceModal && (
                  <MovaticCustomModal
                    useContentDividers
                    open={showClearBalanceModal}
                    onClose={() => setShowClearBalanceModal(false)}
                    title="Clear Balance"
                    content={
                      <div>
                        {`Are you sure that would like to Clear Balance for ${userDetails.fullName}?`}
                      </div>
                    }
                    action={
                      <>
                        <IconButtonMenu
                          buttons={[
                            {
                              label: 'Cancel',
                              onClick: () => setShowClearBalanceModal(false),
                            },
                            {
                              label: 'Submit',
                              onClick: () => {
                                CustomAsyncToast({
                                  promise: () => clearUserBalance(userPhone),
                                  successMessage: () => 'User Balance Cleared',
                                  errorMessage: 'User Balance Clear Failed',
                                  loadingMessage: 'Clearing User Balance...',
                                }).then((r) => {
                                  if (r) {
                                    setShowClearBalanceModal(false);
                                    onSuccess();
                                  }
                                });
                              },
                              startIcon: <SaveIcon />,
                              disabled: !chargeAmount || !chargeAmountDescription,
                            },
                          ]}
                        />
                      </>
                    }
                  />
                )}
              </>
            )}
          </PropertyList>
        }
        title="Payment Information"
        headerActions={
          <IconButtonMenu
            buttonLimit={
              isMobile
                ? 0
                : isMedium
                ? 1
                : calculateArrayBtns.filter((item) => item.shouldRender).length
            }
            slice={
              isMobile
                ? 0
                : isMedium
                ? 1
                : calculateArrayBtns.filter((item) => item.shouldRender).length
            }
            buttons={calculateArrayBtns
              .filter((item) => item.shouldRender)
              .map((item) => ({
                label: item.label,
                onClick: item.onClick,
                startIcon: item.startIcon,
                dataId: item.dataId,
              }))}
          />
        }
      />
      {'systems' in userDetails && userDetails.systems.length > 0 && (
        <CardView
          dataId="systems"
          content={
            <PaginationTable
              dataId="systems-table"
              columns={[{ id: 'name', label: 'Name' }]}
              items={userDetails.systems.map((system) => {
                return {
                  name: system.name,
                };
              })}
              showFilter={false}
            />
          }
          title="Systems Information"
        />
      )}
      {showChargeModal && (
        <>
          <MovaticCustomModal
            open={showChargeModal}
            onClose={() => resetChargeModalFields()}
            title={"Charge User's Card"}
            content={
              <>
                <MovaticFieldGroupOutlinedInput
                  type={'number'}
                  label="Amount"
                  description="How much would you like to charge the user's card?"
                  value={chargeAmount}
                  inputProps={{ max: 9999, min: 0, step: 0.25 }}
                  onChange={(e) => setChargeAmount(e.target.value)}
                  id="amount"
                  name={'amount'}
                  addOn={'$'}
                />
                <MovaticFieldGroupText
                  name="description"
                  label="Charge Description"
                  description="Please insert a charge description"
                  value={chargeAmountDescription}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setChargeAmountDescription(e.target.value);
                  }}
                />
                {!usePartnerName.includes('Movatic') && (
                  <MovaticFieldGroupRadio
                    label="Recipient"
                    description={`Choose if the collected funds will be added to the system account ("${useSystemNames}") or the partner account ("${usePartnerName}").`}
                    value={chargeRecipient}
                    onChange={handleRadioConfigurationChange}
                    options={systemOptions}
                  />
                )}
              </>
            }
            action={
              <IconButtonMenu
                buttons={[
                  {
                    label: 'Cancel',
                    onClick: () => resetChargeModalFields(),
                  },
                  {
                    label: 'Charge',
                    dataId: 'charge-button',
                    onClick: () => {
                      CustomAsyncToast({
                        promise: () =>
                          createCharge({
                            userID: userDetails.id,
                            description: chargeAmountDescription,
                            amount: Math.round(parseFloat(chargeAmount) * 100).toString(),
                            recipient: chargeRecipient,
                          }),
                        successMessage: (r: any) => {
                          if (r.message) {
                            return r.message;
                          }
                          return 'Successfully sent charge request.';
                        },
                        errorMessage: 'Charge Failed',
                        loadingMessage: 'Charging User...',
                      }).then((r) => {
                        if (r) {
                          resetChargeModalFields();
                        }
                      });
                    },
                    startIcon: <PaymentIcon />,
                    disabled: !chargeAmount || !chargeAmountDescription,
                  },
                ]}
              />
            }
          />
        </>
      )}
    </>
  );
};

export default GeneralUserInfoBanner;
